import { SafeEvent } from "@playarena/shared";
import {
  Plus,
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Eye,
  Edit,
  Trash2,
  BarChart3,
  MapPin,
  Target,
  Award,
  AlertCircle,
} from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useAuth } from "../../../hooks/useAuth";
import {
  useGetOrganizerAnalyticsQuery,
  useGetEventStatsQuery,
  useGetBookingStatsQuery,
  useGetRevenueStatsQuery,
  useGetEventPerformanceStatsQuery,
  useGetBookingTrendsQuery,
  useTrackEventMutation,
} from "../../../store/api/analyticsApi";
import { useGetMyEventsQuery } from "../../../store/api/eventApi";
import { formatCurrency, formatDateTime, capitalizeFirst } from "../../../utils/helpers";

const OrganizerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState("overview");

  // Fetch analytics data
  const { data: organizerAnalyticsData, isLoading: organizerAnalyticsLoading } =
    useGetOrganizerAnalyticsQuery();
  const { data: eventStatsData, isLoading: eventStatsLoading } = useGetEventStatsQuery({});
  const { data: bookingStatsData } = useGetBookingStatsQuery({});
  const { data: revenueStatsData } = useGetRevenueStatsQuery({});
  const { data: eventPerformanceData } = useGetEventPerformanceStatsQuery({});
  const { data: bookingTrendsData } = useGetBookingTrendsQuery({});
  const { data: eventsData } = useGetMyEventsQuery({});

  // Track events mutation
  const [trackEvent] = useTrackEventMutation();

  const organizerAnalytics = organizerAnalyticsData?.data;
  const eventStats = eventStatsData?.data;
  const bookingStats = bookingStatsData?.data;
  const revenueStats = revenueStatsData?.data;
  const eventPerformance = eventPerformanceData?.data;
  const bookingTrends = bookingTrendsData?.data || [];
  const events = (eventsData?.data as unknown as (SafeEvent & { revenue: number })[]) || [];

  // Track dashboard view
  useEffect(() => {
    if (user?.id) {
      trackEvent({
        type: "system_health",
        metadata: { userRole: "organizer", action: "dashboard_viewed", timestamp: new Date() },
      });
    }
  }, [user?.id, trackEvent]);

  if (organizerAnalyticsLoading || eventStatsLoading) {
    return (
      <div className='container py-8'>
        <div className='animate-pulse'>
          <div className='mb-4 h-8 w-1/3 rounded bg-gray-200'></div>
          <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-4'>
            {[...Array(4)].map((_, i) => (
              <div key={i} className='h-24 rounded bg-gray-200'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mb-8 flex items-center justify-between'>
        <div>
          <h1 className='mb-2 text-3xl font-bold'>Organizer Dashboard</h1>
          <p className='text-muted-foreground'>Manage your events and track performance.</p>
        </div>
        <Button className='flex items-center gap-2' asChild>
          <Link to='/events/create'>
            <Plus className='h-4 w-4' />
            Create Event
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-4'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Events</CardTitle>
            <Calendar className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{eventStats?.totalEvents || 0}</div>
            <p className='text-xs text-muted-foreground'>
              {eventStats?.upcomingEvents || 0} upcoming
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Active Events</CardTitle>
            <Users className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{eventStats?.activeEvents || 0}</div>
            <p className='text-xs text-muted-foreground'>
              {eventStats?.completedEvents || 0} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Revenue</CardTitle>
            <DollarSign className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {formatCurrency(eventStats?.totalRevenue || 0)}
            </div>
            <p className='text-xs text-muted-foreground'>
              Avg: {formatCurrency(eventStats?.averageTicketPrice || 0)} per ticket
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Avg. Attendance</CardTitle>
            <TrendingUp className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{eventStats?.occupancyRate?.toFixed(1) || 0}%</div>
            <p className='text-xs text-muted-foreground'>
              {Math.round(eventStats?.averageAttendance || 0)} avg attendees
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className='space-y-6'>
        <TabsList>
          <TabsTrigger value='overview'>Overview</TabsTrigger>
          <TabsTrigger value='events'>My Events</TabsTrigger>
          <TabsTrigger value='analytics'>Analytics</TabsTrigger>
          <TabsTrigger value='audience'>Audience</TabsTrigger>
          <TabsTrigger value='performance'>Performance</TabsTrigger>
        </TabsList>

        <TabsContent value='overview' className='space-y-6'>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <Card>
              <CardHeader>
                <CardTitle>Recent Events</CardTitle>
              </CardHeader>
              <CardContent>
                {events.length > 0 ? (
                  <div className='space-y-4'>
                    {events.slice(0, 5).map(event => (
                      <div key={event.id} className='flex items-center justify-between'>
                        <div>
                          <h4 className='font-medium'>{event.title}</h4>
                          <p className='text-sm text-muted-foreground'>
                            {formatDateTime(event.datetime)}
                          </p>
                          <div className='mt-1 flex items-center gap-2'>
                            <Badge
                              variant={
                                event.status === "active"
                                  ? "default"
                                  : event.status === "completed"
                                    ? "default"
                                    : event.status === "scheduled"
                                      ? "secondary"
                                      : "destructive"
                              }
                            >
                              {capitalizeFirst(event.status)}
                            </Badge>
                            <span className='text-xs text-muted-foreground'>
                              {event.bookedSeats || 0}/{event.totalCapacity} seats
                            </span>
                          </div>
                        </div>
                        <div className='text-right'>
                          <div className='font-medium'>{formatCurrency(event.revenue || 0)}</div>
                          <div className='text-sm text-muted-foreground'>
                            {(((event.bookedSeats || 0) / event.totalCapacity) * 100).toFixed(1)}%
                            filled
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='py-8 text-center'>
                    <Calendar className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                    <p className='text-muted-foreground'>No events created yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <Button className='w-full justify-start bg-transparent' variant='outline' asChild>
                  <Link to='/events/create'>
                    <Plus className='mr-2 h-4 w-4' />
                    Create New Event
                  </Link>
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline' asChild>
                  <Link to='/events'>
                    <Calendar className='mr-2 h-4 w-4' />
                    View Calendar
                  </Link>
                </Button>
                <Button
                  className='w-full justify-start bg-transparent'
                  variant='outline'
                  onClick={() => setSelectedTab("analytics")}
                >
                  <TrendingUp className='mr-2 h-4 w-4' />
                  View Analytics
                </Button>
                <Button
                  className='w-full justify-start bg-transparent'
                  variant='outline'
                  onClick={() => setSelectedTab("performance")}
                >
                  <BarChart3 className='mr-2 h-4 w-4' />
                  Performance Report
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Top Performing Events */}
          {organizerAnalytics?.eventPerformance?.topPerformingEvents && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Award className='h-5 w-5' />
                  Top Performing Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {organizerAnalytics.eventPerformance.topPerformingEvents
                    .slice(0, 5)
                    .map((event: any) => (
                      <div key={event.eventId} className='flex items-center justify-between'>
                        <div>
                          <h4 className='font-medium'>{event.eventTitle}</h4>
                          <p className='text-sm text-muted-foreground'>
                            {event.totalBookings} bookings •{" "}
                            {event.averageRating?.toFixed(1) || "N/A"} ★
                          </p>
                        </div>
                        <div className='text-right'>
                          <div className='font-medium text-green-600'>
                            {formatCurrency(event.totalRevenue)}
                          </div>
                          <Badge variant='default'>Best Seller</Badge>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revenue Analytics Summary */}
          {organizerAnalytics?.revenueAnalytics && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <DollarSign className='h-5 w-5' />
                  Revenue Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {organizerAnalytics.revenueAnalytics.slice(-6).map((month: any) => (
                    <div key={month.month} className='flex items-center justify-between'>
                      <span className='font-medium'>
                        {month.month} {month.year}
                      </span>
                      <div className='flex items-center gap-4'>
                        <span className='text-sm text-muted-foreground'>{month.count} events</span>
                        <span className='font-medium text-green-600'>
                          {formatCurrency(month.revenue)}
                        </span>
                        {month.growth !== undefined && (
                          <span
                            className={`text-xs ${month.growth >= 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {month.growth > 0 ? "+" : ""}
                            {month.growth.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='events' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>All Events ({events.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {events.length > 0 ? (
                <div className='space-y-4'>
                  {events.map(event => (
                    <div
                      key={event.id}
                      className='flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-gray-50'
                    >
                      <div className='flex-1'>
                        <h4 className='font-medium'>{event.title}</h4>
                        <p className='line-clamp-1 text-sm text-muted-foreground'>
                          {event.description}
                        </p>
                        <div className='mt-2 flex items-center gap-4 text-sm text-muted-foreground'>
                          <span className='flex items-center gap-1'>
                            <Calendar className='h-3 w-3' />
                            {formatDateTime(event.datetime)}
                          </span>
                          <span className='flex items-center gap-1'>
                            <MapPin className='h-3 w-3' />
                            {event.ground?.location.city}
                          </span>
                          <span className='flex items-center gap-1'>
                            <Users className='h-3 w-3' />
                            {event.bookedSeats || 0}/{event.totalCapacity}
                          </span>
                        </div>
                        {event.tags && event.tags.length > 0 && (
                          <div className='mt-2 flex gap-1'>
                            {event.tags.slice(0, 3).map(tag => (
                              <Badge key={tag} variant='secondary' className='text-xs'>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='mr-4 text-right'>
                          <div className='font-medium text-green-600'>
                            {formatCurrency(event.revenue || 0)}
                          </div>
                          <Badge variant={event.status === "active" ? "default" : "secondary"}>
                            {capitalizeFirst(event.status)}
                          </Badge>
                        </div>
                        <Button size='sm' variant='outline' asChild>
                          <Link to={`/events/${event.id}`}>
                            <Eye className='h-4 w-4' />
                          </Link>
                        </Button>
                        <Button size='sm' variant='outline' asChild>
                          <Link to={`/events/${event.id}/edit`}>
                            <Edit className='h-4 w-4' />
                          </Link>
                        </Button>
                        <Button size='sm' variant='outline'>
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Calendar className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='mb-4 text-muted-foreground'>No events created yet</p>
                  <Button asChild>
                    <Link to='/events/create'>
                      <Plus className='mr-2 h-4 w-4' />
                      Create Your First Event
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='analytics' className='space-y-4'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
            <Card>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <CardTitle className='text-sm font-medium'>Conversion Rate</CardTitle>
                <Target className='h-4 w-4 text-muted-foreground' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>
                  {bookingStats?.conversionRate?.toFixed(1) || 0}%
                </div>
                <p className='text-xs text-muted-foreground'>Views to bookings</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <CardTitle className='text-sm font-medium'>Cancellation Rate</CardTitle>
                <AlertCircle className='h-4 w-4 text-muted-foreground' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>
                  {bookingStats?.cancellationRate?.toFixed(1) || 0}%
                </div>
                <p className='text-xs text-muted-foreground'>
                  {bookingStats?.cancelledBookings || 0} cancelled
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <CardTitle className='text-sm font-medium'>Avg Ticket Price</CardTitle>
                <DollarSign className='h-4 w-4 text-muted-foreground' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold'>
                  {formatCurrency(eventStats?.averageTicketPrice || 0)}
                </div>
                <p className='text-xs text-muted-foreground'>Per ticket sold</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <CardTitle className='text-sm font-medium'>Revenue Growth</CardTitle>
                <TrendingUp className='h-4 w-4 text-muted-foreground' />
              </CardHeader>
              <CardContent>
                <div className='text-2xl font-bold text-green-600'>
                  +{revenueStats?.revenueGrowth?.toFixed(1) || 0}%
                </div>
                <p className='text-xs text-muted-foreground'>This month</p>
              </CardContent>
            </Card>
          </div>

          {/* Event Performance Chart */}
          {eventStats?.eventsByMonth && (
            <Card>
              <CardHeader>
                <CardTitle>Event Creation Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {eventStats.eventsByMonth.slice(-6).map((month: any) => (
                    <div key={month.month} className='flex items-center justify-between'>
                      <span className='font-medium'>{month.month}</span>
                      <div className='flex items-center gap-4'>
                        <span className='text-sm text-muted-foreground'>{month.count} events</span>
                        <span className='font-medium'>{formatCurrency(month.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Booking Trends */}
          {bookingTrends && bookingTrends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Booking Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {bookingTrends.slice(-7).map((trend: any, index: number) => (
                    <div key={index} className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>{trend.date}</span>
                      <div className='flex items-center gap-4'>
                        <span className='text-sm text-muted-foreground'>
                          {trend.bookings} bookings
                        </span>
                        <span className='text-sm font-medium'>{formatCurrency(trend.revenue)}</span>
                        <span className='text-xs text-muted-foreground'>
                          Avg: {formatCurrency(trend.averageBookingValue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='audience' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>Audience Insights</CardTitle>
            </CardHeader>
            <CardContent>
              {organizerAnalytics?.audienceInsights ? (
                <div className='space-y-6'>
                  {/* Age Groups */}
                  <div>
                    <h3 className='mb-3 font-semibold'>Age Distribution</h3>
                    <div className='space-y-2'>
                      {Object.entries(organizerAnalytics.audienceInsights.ageGroups || {}).map(
                        ([age, count]) => (
                          <div key={age} className='flex items-center justify-between'>
                            <span>{age}</span>
                            <div className='flex items-center gap-2'>
                              <div className='h-2 w-32 rounded-full bg-gray-200'>
                                <div
                                  className='h-2 rounded-full bg-blue-600'
                                  style={{
                                    width: `${((count as number) / Object.values(organizerAnalytics.audienceInsights.ageGroups).reduce((a: number, b: any) => a + b, 0)) * 100}%`,
                                  }}
                                />
                              </div>
                              <span className='font-medium'>{count as number}</span>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Location Distribution */}
                  <div>
                    <h3 className='mb-3 font-semibold'>Top Locations</h3>
                    <div className='space-y-2'>
                      {organizerAnalytics.audienceInsights.locationDistribution
                        ?.slice(0, 5)
                        .map((location: any) => (
                          <div key={location.country} className='flex justify-between'>
                            <span>{location.country}</span>
                            <span className='font-medium'>
                              {location.count} ({location.percentage.toFixed(1)}%)
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Ticket Type Preferences */}
                  <div>
                    <h3 className='mb-3 font-semibold'>Popular Ticket Types</h3>
                    <div className='space-y-2'>
                      {Object.entries(
                        organizerAnalytics.audienceInsights.ticketTypeDistribution || {}
                      ).map(([type, count]) => (
                        <div key={type} className='flex justify-between'>
                          <span className='capitalize'>{type.replace(/_/g, " ")}</span>
                          <span className='font-medium'>{count as number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Users className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='text-muted-foreground'>
                    Audience insights will appear after you have some bookings
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='performance' className='space-y-4'>
          {eventPerformance ? (
            <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
              <Card>
                <CardHeader>
                  <CardTitle>Event Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-4'>
                    <div className='flex justify-between'>
                      <span>Total Events:</span>
                      <span className='font-medium'>{eventPerformance.totalEvents}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Average Attendance:</span>
                      <span className='font-medium'>
                        {eventPerformance.averageAttendance?.toFixed(0) || 0}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Occupancy Rate:</span>
                      <span className='font-medium text-green-600'>
                        {eventPerformance.occupancyRate?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Total Revenue:</span>
                      <span className='font-medium text-green-600'>
                        {formatCurrency(eventPerformance.totalRevenue || 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Event Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    {Object.entries(eventPerformance.eventsByCategory || {}).map(
                      ([category, count]) => (
                        <div key={category} className='flex justify-between'>
                          <span className='capitalize'>{category}</span>
                          <Badge variant='secondary'>{count as number}</Badge>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Performance Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='py-8 text-center'>
                  <BarChart3 className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='text-muted-foreground'>
                    Performance data will be available after your events have some activity
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Competitor Analysis */}
          {organizerAnalytics?.competitorAnalysis && (
            <Card>
              <CardHeader>
                <CardTitle>Market Position</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  <div className='flex justify-between'>
                    <span>Your Avg Ticket Price:</span>
                    <span className='font-medium'>
                      {formatCurrency(eventStats?.averageTicketPrice || 0)}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Market Average:</span>
                    <span className='font-medium'>
                      {formatCurrency(organizerAnalytics.competitorAnalysis.averageTicketPrice)}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Market Share:</span>
                    <span className='font-medium'>
                      {organizerAnalytics.competitorAnalysis.marketShare?.toFixed(2) || 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrganizerDashboard;
